import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { EOrgType } from '@unfoldrtech/portal-mic'
import {
  EChannelTypeGlobal,
  EParamChannelTypeGlobal,
  EReportColumnHeader,
  EReportingTimeWindow,
  IDAReportSearchTermsTable,
  ISPReportSearchTermsTable,
} from '@unfoldrtech/portal-mic/dist/src/models/core'

import { ReportPageTemplateLayout } from '../../components/ReportPageTemplateLayout'
import { TReportExportType, TReportingFilters } from '../../../../models'
import { AppContext, AdGroupContext, CampaignContext } from '../../../../models/contexts'
import {
  selectReportingFilters,
  selectSearchTermsTableReportingFilters,
  setIsReportingV3,
} from '../../../../store/reportingFilters'
import {
  NOT_AVAILABLE,
  exportFileDateFormat,
} from '../../../../utils/constants'
import { EXPORT_PAGE_LIMIT, exportTableDataToFile } from '../../helpers'
import { useGetDatasetKeyTableWhitelist } from '../../hooks/use-get-dataset-key-table-whitelist'
import {
  convertChannelTypeToParam,
  convertEParamChannelTypeToEChannelTypeGlobal,
  generateCorrelationId,
  getTimezone,
} from '../../../../utils/helpers'
import { getSearchTermsEDatasetKeyFromChannelTypeGlobal } from '../helpers/get-search-terms-e-dataset-key-from-channel-type-global'
import { TDatasetWhitelistKey } from '../../models'
import { sanitizeDataTestId } from '../../../../utils'
import { ReportingSearchTermsTableWrapper } from './ReportingSearchTermsTableWrapper'
import { coreAPIV3 } from '../../../../services/coreAPIV3'
import { useGetRetailerAndAdvertiserIds } from '../../hooks/use-get-retailer-and-advertiser-ids'
import { ErrorToast } from '../../../ToastCard/ErrorToast'
import { SuccessToast } from '../../../ToastCard/SuccessToast'

export const ReportingSearchTermsTemplate = ({
  channelType: propChannelType,
}: {
  channelType?: EChannelTypeGlobal
}) => {
  const pageTitle = propChannelType
    ? `reporting.header.search.terms.${sanitizeDataTestId(propChannelType)}`
    : 'reporting.header.search.terms'

  const intl = useIntl()
  const dispatch = useDispatch()

  const [appContext] = useContext(AppContext)
  const campaign = useContext(CampaignContext)
  const adGroup = useContext(AdGroupContext)
  const { channelType: reportingFilterChannelType } =
    useSelector(selectReportingFilters)
  const filters = useSelector(selectSearchTermsTableReportingFilters)
  const channelType =
    propChannelType ||
    convertEParamChannelTypeToEChannelTypeGlobal(
      reportingFilterChannelType ||
        convertChannelTypeToParam(campaign.channelType)
    )

  const { retailerId, advertiserId } = useGetRetailerAndAdvertiserIds()
  const orgType = appContext.userOrg!.type

  const [isExporting, setIsExporting] = useState(false)
  const [showExportErrorToast, setShowExportErrorToast] = useState(false)
  const [showExportCappedToast, setShowExportCappedToast] = useState(false)

  const [tableData, setTableData] = useState<
    ISPReportSearchTermsTable | IDAReportSearchTermsTable
  >()

  const onTableDataChange = (
    searchTermsTableData: ISPReportSearchTermsTable | IDAReportSearchTermsTable
  ) => {
    setTableData(searchTermsTableData)
  }
  const whitelistData = useGetDatasetKeyTableWhitelist({
    key:
      (tableData?.datasetKey as TDatasetWhitelistKey) ||
      getSearchTermsEDatasetKeyFromChannelTypeGlobal(channelType),
  })

  const onExportData = async (
    { startDate: start, endDate: end }: TReportingFilters,
    type: TReportExportType
  ) => {
    const orgName =
      appContext.userOrg?.name ?? appContext.userOrg?.type ?? NOT_AVAILABLE
    const translatedPageTitle = intl
      .formatMessage({ id: pageTitle })
      .replace(/[-]+/g, '')
      .replace(/[ ]+/g, '_')
    const sDate = dayjs(start).format(exportFileDateFormat)
    const eDate = dayjs(end).format(exportFileDateFormat)

    const fileName = `${orgName}_${translatedPageTitle}_${sDate}_${eDate}`
    setIsExporting(true)
    setShowExportErrorToast(false)
    setShowExportCappedToast(false)
    try {
      const res =
        orgType === EOrgType.Advertiser
          ? await coreAPIV3.advertiserReporting().getAdvertiserSearchTermsReportTable(
              advertiserId,
              generateCorrelationId(),
              retailerId,
              campaign?.id,
              adGroup?.id,
              channelType as unknown as EParamChannelTypeGlobal,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as unknown as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined
            )
          : await coreAPIV3.retailerReporting().getRetailerSearchTermsReportTable(
              retailerId,
              generateCorrelationId(),
              advertiserId,
              campaign?.id,
              adGroup?.id,
              channelType as unknown as EParamChannelTypeGlobal,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined
            )

      const rows = res.data.table?.rows ?? []

      exportTableDataToFile({
        tableDataRows: rows,
        tableHeaders: whitelistData,
        fileName,
        fileType: type,
      })

      if (rows.length >= EXPORT_PAGE_LIMIT) {
        setShowExportCappedToast(true)
      }
    } catch {
      setShowExportErrorToast(true)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    dispatch(setIsReportingV3({ IsReportingV3: true }))
  }, [])

  return (
    <>
      <ReportPageTemplateLayout
        title={pageTitle}
        subTitle="search.terms.description"
        isExportDisabled={isExporting || !tableData?.datasetKey}
        chartWrapper={<></>}
        tableWrapper={
          <ReportingSearchTermsTableWrapper
            onTableDataChange={onTableDataChange}
            channelType={channelType!}
          />
        }
        onExportData={onExportData}
      />
      <SuccessToast
        show={showExportCappedToast}
        onClose={() => setShowExportCappedToast(false)}
        translationKey="reporting.export.capped"
      />
      <ErrorToast
        show={showExportErrorToast}
        onClose={() => setShowExportErrorToast(false)}
        translationKey="reporting.export.failed"
      />
    </>
  )
}
